import React, { useState, useEffect } from 'react';
import { Save, FolderOpen, Trash2, X, Download, Upload } from 'lucide-react';

function ProjectManager({ 
  murs, 
  plaqueDimensions, 
  onLoadProject,
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [savedProjects, setSavedProjects] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Charger la liste des projets sauvegardés au montage
  useEffect(() => {
    loadProjectList();
  }, []);

  const loadProjectList = () => {
    const projects = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('placoplatre_project_')) {
        try {
          const projectData = JSON.parse(localStorage.getItem(key));
          projects.push({
            id: key.replace('placoplatre_project_', ''),
            name: projectData.name || 'Projet sans nom',
            date: projectData.savedAt || new Date().toISOString(),
            key: key
          });
        } catch (e) {
          console.error('Erreur lors du chargement du projet:', key, e);
        }
      }
    }
    // Trier par date décroissante
    projects.sort((a, b) => new Date(b.date) - new Date(a.date));
    setSavedProjects(projects);
  };

  const saveProject = () => {
    if (!projectName.trim()) {
      alert('Veuillez entrer un nom pour le projet');
      return;
    }

    const projectData = {
      name: projectName.trim(),
      savedAt: new Date().toISOString(),
      version: '1.0',
      murs: murs,
      plaqueDimensions: plaqueDimensions
    };

    const key = `placoplatre_project_${Date.now()}`;
    localStorage.setItem(key, JSON.stringify(projectData));
    
    loadProjectList();
    setProjectName('');
    setShowSaveDialog(false);
    alert('Projet sauvegardé avec succès !');
  };

  const loadProject = (projectKey) => {
    try {
      const projectData = JSON.parse(localStorage.getItem(projectKey));
      if (projectData && projectData.murs && projectData.plaqueDimensions) {
        onLoadProject(projectData.murs, projectData.plaqueDimensions);
        setShowLoadDialog(false);
        setIsOpen(false);
        alert(`Projet "${projectData.name}" chargé avec succès !`);
      } else {
        alert('Données du projet invalides');
      }
    } catch (e) {
      console.error('Erreur lors du chargement du projet:', e);
      alert('Erreur lors du chargement du projet');
    }
  };

  const deleteProject = (projectKey) => {
    localStorage.removeItem(projectKey);
    setConfirmDelete(null);
    loadProjectList();
  };

  const exportToFile = (projectKey) => {
    try {
      const projectData = localStorage.getItem(projectKey);
      const project = JSON.parse(projectData);
      
      const blob = new Blob([projectData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.placoplatre.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Erreur lors de l\'export:', e);
      alert('Erreur lors de l\'export du projet');
    }
  };

  const importFromFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const projectData = JSON.parse(e.target.result);
        if (projectData && projectData.murs && projectData.plaqueDimensions) {
          // Sauvegarder dans le localStorage
          const key = `placoplatre_project_${Date.now()}`;
          localStorage.setItem(key, JSON.stringify({
            ...projectData,
            name: `${projectData.name || 'Projet importé'} (importé)`,
            savedAt: new Date().toISOString()
          }));
          loadProjectList();
          alert('Projet importé avec succès !');
        } else {
          alert('Fichier invalide');
        }
      } catch (err) {
        console.error('Erreur lors de l\'import:', err);
        alert('Erreur lors de l\'import du fichier');
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={className}>
      <button 
        className="btn btn-secondary"
        onClick={() => setIsOpen(true)}
        title="Gérer les projets"
      >
        <FolderOpen size={18} style={{ marginRight: '8px' }} />
        Projets
      </button>

      {isOpen && (
        <div className="modal-overlay" onClick={() => setIsOpen(false)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Gestion des projets</h3>
              <button className="btn-icon" onClick={() => setIsOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="project-actions" style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    setShowSaveDialog(true);
                    setShowLoadDialog(false);
                  }}
                >
                  <Save size={18} style={{ marginRight: '8px' }} />
                  Sauvegarder
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowLoadDialog(true);
                    setShowSaveDialog(false);
                  }}
                >
                  <FolderOpen size={18} style={{ marginRight: '8px' }} />
                  Charger
                </button>
                <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                  <Upload size={18} style={{ marginRight: '8px' }} />
                  Importer
                  <input 
                    type="file" 
                    accept=".json"
                    style={{ display: 'none' }}
                    onChange={importFromFile}
                  />
                </label>
              </div>

              {showSaveDialog && (
                <div className="save-dialog" style={{ 
                  padding: '15px', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '8px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ marginTop: 0 }}>Sauvegarder le projet</h4>
                  <div className="form-group">
                    <label>Nom du projet</label>
                    <input
                      type="text"
                      className="form-control"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      placeholder="Mon projet de rénovation"
                      onKeyPress={(e) => e.key === 'Enter' && saveProject()}
                      autoFocus
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <button className="btn btn-primary" onClick={saveProject}>
                      Sauvegarder
                    </button>
                    <button 
                      className="btn btn-secondary" 
                      onClick={() => {
                        setShowSaveDialog(false);
                        setProjectName('');
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              {showLoadDialog && (
                <div className="load-dialog">
                  <h4>Projets sauvegardés</h4>
                  {savedProjects.length === 0 ? (
                    <p style={{ color: '#666', fontStyle: 'italic' }}>
                      Aucun projet sauvegardé
                    </p>
                  ) : (
                    <div className="projects-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {savedProjects.map((project) => (
                        <div 
                          key={project.key}
                          className="project-item"
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px',
                            borderBottom: '1px solid #eee',
                            backgroundColor: confirmDelete === project.key ? '#fff3cd' : 'transparent'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold' }}>{project.name}</div>
                            <div style={{ fontSize: '0.85em', color: '#666' }}>
                              {formatDate(project.date)}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            {confirmDelete === project.key ? (
                              <>
                                <span style={{ fontSize: '0.85em', color: '#dc3545', marginRight: '5px' }}>
                                  Confirmer ?
                                </span>
                                <button 
                                  className="btn btn-sm btn-danger"
                                  onClick={() => deleteProject(project.key)}
                                >
                                  Oui
                                </button>
                                <button 
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => setConfirmDelete(null)}
                                >
                                  Non
                                </button>
                              </>
                            ) : (
                              <>
                                <button 
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => exportToFile(project.key)}
                                  title="Exporter"
                                >
                                  <Download size={14} />
                                </button>
                                <button 
                                  className="btn btn-sm btn-primary"
                                  onClick={() => loadProject(project.key)}
                                >
                                  Charger
                                </button>
                                <button 
                                  className="btn btn-sm btn-danger"
                                  onClick={() => setConfirmDelete(project.key)}
                                  title="Supprimer"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectManager;
